import React from 'react';
import parse, {
  HTMLReactParserOptions,
  Element,
  Text,
  domToReact,
  DOMNode,
  Element as ReactElement,
} from 'html-react-parser';

// Define the types for component generation
export type ComponentGenerator = (props: any) => React.ReactNode;

export interface KeywordMapping {
  keyword: string; // The text pattern to look for (e.g. "[[applyButton]]")
  component: ComponentGenerator; // Function that returns the component
  props?: Record<string, any>; // Additional props to pass to the component
  extractParams?: boolean; // Whether to extract JSON parameters after the keyword
  processNestedContent?: boolean; // Flag to enable processing keywords in the component's content
}

interface ParseOptions {
  preview?: boolean;
  setSignUpRequiredOpen?: any;
  keywordMappings: KeywordMapping[]; // Array of keyword mappings
  event?: any; // The event object if parsing an event description
  eventOffers?: any[]; // List of offers associated with the event
}

// Helper to check if a node is a Text node with a specific keyword
const hasKeywordInTextNode = (node: DOMNode, keywords: string[]): node is Text => {
  if (!(node.type === 'text' && 'data' in node && typeof node.data === 'string')) {
    return false;
  }

  return keywords.some((keyword) => node.data.includes(keyword));
};

// Define types for our custom nodes
type TextNodeWithData = Text & {data: string};
type ComponentMarkerNode = {
  type: 'component-marker';
  key: string;
  keyword: string;
  params?: any; // Store extracted JSON params
};
type CustomNode = TextNodeWithData | ComponentMarkerNode | DOMNode;

// Helper function to extract JSON from HTML with TinyMCE-style divs
const extractJsonFromDivs = (html: string, keyword: string): string | null => {
  if (!html || !html.includes(keyword)) return null;

  try {
    // Find the position of the keyword in the HTML
    const keywordPos = html.indexOf(keyword);
    if (keywordPos === -1) return null;

    const afterKeyword = html.substring(keywordPos + keyword.length);

    // Check if we have what looks like multiple divs containing JSON parts
    if (
      afterKeyword.includes('<div>') &&
      (afterKeyword.includes('&quot;') || afterKeyword.includes('"'))
    ) {
      // This is a heuristic to detect TinyMCE-style formatting
      const maybeJsonStart = afterKeyword.indexOf('<div>');

      // Find a closing </div> tag after the last JSON property
      let jsonEndPos = afterKeyword.indexOf('</div>', maybeJsonStart);
      let openingDivs = (afterKeyword.substring(0, jsonEndPos).match(/<div>/g) || []).length;
      let closingDivs = 1; // We've found one closing div

      // Keep looking for closing divs until we've matched all opening divs
      // This helps find the end of the JSON structure spread across multiple divs
      while (closingDivs < openingDivs && jsonEndPos > 0) {
        const nextClosingDiv = afterKeyword.indexOf('</div>', jsonEndPos + 6);
        if (nextClosingDiv === -1) break;
        jsonEndPos = nextClosingDiv;
        closingDivs++;
      }

      // Extract the text between and including these divs
      let jsonDivs = afterKeyword.substring(maybeJsonStart, jsonEndPos + 6);

      // Clean up the extracted text to create valid JSON
      const jsonStr = jsonDivs
        .replace(/<div>&nbsp;&nbsp;&nbsp;/g, '') // Remove div starts with spaces
        .replace(/<div>&nbsp;&nbsp;/g, '') // Handle 2 spaces too
        .replace(/<div>/g, '') // Remove all other div starts
        .replace(/<\/div>/g, '') // Remove all div ends
        .replace(/&nbsp;/g, '') // Remove HTML spaces
        .replace(/&quot;/g, '"') // Replace &quot; with "
        .replace(/&lt;/g, '<') // Handle other common HTML entities
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .trim();

      console.log('Extracted JSON-like content from divs:', jsonStr);
      return jsonStr;
    }
  } catch (error) {
    console.error('Error extracting JSON from divs:', error);
  }

  return null;
};

export const parseHtmlWithComponents = (html: string, options: ParseOptions) => {
  const {preview = false, setSignUpRequiredOpen, keywordMappings = []} = options;

  if (!html) return null;
  // PRE-PROCESS: Extract JSON parameters from raw HTML BEFORE html-react-parser processes it
  // This avoids the truncation issue where html-react-parser cuts off JSON content
  const preExtractedParams: Record<string, any[]> = {}; // Changed to array to handle multiple instances
  let cleanedHtml = html; // We'll remove the JSON from this

  if (keywordMappings.length > 0) {
    for (const mapping of keywordMappings) {
      if (mapping.extractParams && cleanedHtml.includes(mapping.keyword)) {
        // Initialize array for this keyword if not exists
        if (!preExtractedParams[mapping.keyword]) {
          preExtractedParams[mapping.keyword] = [];
        }

        let searchText = cleanedHtml;
        let globalOffset = 0;

        // Find ALL instances of this keyword, not just the first one
        while (searchText.includes(mapping.keyword)) {
          const keywordPos = searchText.indexOf(mapping.keyword);
          const absoluteKeywordPos = globalOffset + keywordPos;
          let remainingText = searchText.substring(keywordPos + mapping.keyword.length).trim();

          if (remainingText.startsWith('[') || remainingText.startsWith('{')) {
            let openBracket = remainingText[0];
            let closeBracket = openBracket === '[' ? ']' : '}';
            let depth = 1;
            let jsonEndPos = -1;
            let inString = false;
            let escapeNext = false;

            // Use the working bracket matching algorithm
            for (let i = 1; i < remainingText.length; i++) {
              const char = remainingText[i];

              if (escapeNext) {
                escapeNext = false;
                continue;
              }

              if (char === '\\') {
                escapeNext = true;
                continue;
              }

              if (char === '"' && !escapeNext) {
                inString = !inString;
                continue;
              }

              if (!inString) {
                if (char === openBracket) {
                  depth++;
                } else if (char === closeBracket) {
                  depth--;
                  if (depth === 0) {
                    jsonEndPos = i;
                    break;
                  }
                }
              }
            }

            if (jsonEndPos !== -1) {
              let jsonStr = remainingText.substring(0, jsonEndPos + 1);

              // Clean the JSON string
              let cleanedJson = jsonStr
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&nbsp;/g, ' ')
                .replace(/\r\n/g, ' ')
                .replace(/\n/g, ' ')
                .replace(/\r/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

              // Handle unescaped quotes in HTML attributes
              cleanedJson = cleanedJson.replace(
                /(\s+\w+)="([^"]*?)"/g,
                (match, attrName, attrValue) => {
                  return `${attrName}=\\"${attrValue}\\"`;
                }
              );

              try {
                const parsedJson = JSON.parse(cleanedJson);
                preExtractedParams[mapping.keyword].push(parsedJson);

                // REMOVE only the JSON from the HTML, but KEEP the keyword so components can be rendered
                const beforeJson = cleanedHtml.substring(0, absoluteKeywordPos + mapping.keyword.length);
                const afterJson = cleanedHtml.substring(
                  absoluteKeywordPos + mapping.keyword.length + jsonStr.length
                );
                cleanedHtml = beforeJson + afterJson;

                // Update searchText to continue searching after this instance
                // Account for the removed JSON length
                const removedLength = jsonStr.length;
                searchText = cleanedHtml.substring(absoluteKeywordPos + mapping.keyword.length);
                globalOffset = absoluteKeywordPos + mapping.keyword.length;
              } catch (parseError) {
                console.error(
                  `❌ PRE-EXTRACTION JSON parse error for ${mapping.keyword}:`,
                  parseError
                );
                // Move past this keyword to continue searching
                searchText = searchText.substring(keywordPos + mapping.keyword.length);
                globalOffset += keywordPos + mapping.keyword.length;
              }
            } else {
              // No valid JSON found, move past this keyword
              searchText = searchText.substring(keywordPos + mapping.keyword.length);
              globalOffset += keywordPos + mapping.keyword.length;
            }
          } else {
            // No JSON after keyword, move past this keyword
            searchText = searchText.substring(keywordPos + mapping.keyword.length);
            globalOffset += keywordPos + mapping.keyword.length;
          }        }
      }
    }
  }  // Store options in a local variable so other functions can access it
  const parseOptions = options;
  if (!keywordMappings.length) return <div dangerouslySetInnerHTML={{__html: html}} />;

  // Extract all keywords for easy checking
  const keywords = keywordMappings.map((mapping) => mapping.keyword);

  // Counter to track which instance of each keyword we're processing
  const keywordInstanceCounters: Record<string, number> = {};
  keywords.forEach(keyword => {
    keywordInstanceCounters[keyword] = 0;
  });

  // REMOVED TinyMCE preprocessing - it was causing keyword-to-JSON confusion
  // The extractJsonParams function now handles all JSON extraction inline// Function to extract JSON parameters after a keyword
  const extractJsonParams = (
    text: string,
    keyword: string
  ): {jsonParams: any; remainingText: string; jsonLength: number} => {
    const keywordPos = text.indexOf(keyword);
    if (keywordPos === -1) return {jsonParams: null, remainingText: text, jsonLength: 0};

    let remainingText = text.substring(keywordPos + keyword.length);

    // Trim leading whitespace
    const originalLength = remainingText.length;
    remainingText = remainingText.trim();
    const leadingWhitespaceLength = originalLength - remainingText.length;

    // If there's no content after the keyword, return no params
    if (!remainingText) return {jsonParams: null, remainingText: '', jsonLength: 0};

    // Special case for offerCard: Extract the simple ID number if it doesn't start with JSON
    if (
      keyword === '[[offerCard]]' &&
      !remainingText.startsWith('{') &&
      !remainingText.startsWith('[')
    ) {
      // Find the first non-numeric character or whitespace
      let idEndPos = 0;
      while (idEndPos < remainingText.length && /[0-9]/.test(remainingText[idEndPos])) {
        idEndPos++;
      }

      if (idEndPos > 0) {
        const offerId = remainingText.substring(0, idEndPos);

        return {
          jsonParams: {id: offerId},
          remainingText: remainingText.substring(idEndPos),
          jsonLength: idEndPos + leadingWhitespaceLength,
        };
      }
    }

    try {
      // Check if the content starts with a JSON-like structure
      if (remainingText.startsWith('[') || remainingText.startsWith('{')) {
        let openBracket = remainingText[0];
        let closeBracket = openBracket === '[' ? ']' : '}';
        let bracketCount = 1;
        let jsonEndPos = -1;
        let inString = false;
        let escapeNext = false;
        // More robust JSON boundary detection that handles strings properly
        for (let i = 1; i < remainingText.length; i++) {
          const char = remainingText[i];
          if (escapeNext) {
            escapeNext = false;
            continue;
          }

          if (char === '\\') {
            escapeNext = true;
            continue;
          }

          if (char === '"' && !escapeNext) {
            inString = !inString;
            continue;
          }
          if (!inString) {
            if (char === openBracket) bracketCount++;
            if (char === closeBracket) bracketCount--;
            if (bracketCount === 0) {
              jsonEndPos = i;
              break;
            }
          }
        }

        // DEBUG: Log completion status for placeListings
        if (keyword === '[[placeListings]]') {
          console.log(
            `🔍 DEBUG: Bracket matching completed for placeListings. jsonEndPos: ${jsonEndPos}, final bracketCount: ${bracketCount}`
          );
          if (jsonEndPos === -1) {
            console.log(
              `🔍 DEBUG: Never found bracketCount === 0. Text length: ${remainingText.length}`
            );
            console.log(
              `🔍 DEBUG: Last 200 chars of text:`,
              remainingText.substring(remainingText.length - 200)
            );
          }
        }

        if (jsonEndPos !== -1) {
          let jsonStr = remainingText.substring(0, jsonEndPos + 1);

          // STEP 1: Handle TinyMCE's escaped quotes FIRST, before any other processing
          jsonStr = jsonStr.replace(/\\"/g, '"');

          // STEP 2: Handle common HTML entities (from backend HTML encoding)
          jsonStr = jsonStr
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/&eacute;/g, 'é')
            .replace(/&ntilde;/g, 'ñ')
            .replace(/&agrave;/g, 'à')
            .replace(/&aacute;/g, 'á')
            .replace(/&iacute;/g, 'í')
            .replace(/&oacute;/g, 'ó')
            .replace(/&uacute;/g, 'ú'); // STEP 3: CRITICAL FIX - Handle unescaped quotes in HTML attributes within JSON string values
          // The main issue is HTML like: <div style="padding: 10px; color: red;">
          // These quotes within HTML attributes break JSON parsing

          // We need to escape quotes that appear within HTML attributes in JSON string values
          // This regex matches HTML attributes like style="..." or class="..." and escapes the quotes
          jsonStr = jsonStr.replace(/(\w+)=\\"([^"]*?)\\"/g, (match, attrName, attrValue) => {
            // This handles already escaped quotes in attributes - keep them escaped
            return match;
          });

          // Now handle unescaped quotes in HTML attributes within JSON string values
          // This regex finds patterns like: attribute="value" within JSON strings
          jsonStr = jsonStr.replace(/(\s+\w+)="([^"]*?)"/g, (match, attrName, attrValue) => {
            // Escape the quotes around the attribute value for JSON compatibility
            return `${attrName}=\\"${attrValue}\\"`;
          });

          // Remove line breaks that break JSON parsing
          jsonStr = jsonStr.replace(/\r\n/g, ' ').replace(/\n/g, ' ').replace(/\r/g, ' ');

          // Clean up extra whitespace
          jsonStr = jsonStr.replace(/\s+/g, ' ').trim();

          try {
            // Parse the JSON - HTML attributes are now properly escaped
            const parsedJson = JSON.parse(jsonStr);
            // Additional cleanup for any remaining HTML entities in the parsed content
            const cleanHtmlEntities = (obj: any): any => {
              if (!obj) return obj;

              if (typeof obj === 'string') {
                return obj
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'")
                  .replace(/&#34;/g, '"')
                  .replace(/&nbsp;/g, ' ');
              }

              if (Array.isArray(obj)) {
                return obj.map((item) => cleanHtmlEntities(item));
              }

              if (typeof obj === 'object') {
                const result: any = {};
                for (const key in obj) {
                  result[key] = cleanHtmlEntities(obj[key]);
                }
                return result;
              }

              return obj;
            };

            // Clean any remaining HTML entities
            const finalJson = cleanHtmlEntities(parsedJson);

            // Return the length including the JSON string and any leading whitespace
            const jsonLength = jsonEndPos + 1 + leadingWhitespaceLength;
            return {
              jsonParams: finalJson,
              remainingText: remainingText.substring(jsonEndPos + 1),
              jsonLength,
            };
          } catch (parseError) {
            console.error(`❌ Invalid JSON after keyword ${keyword}:`, parseError);
            console.error('JSON string that failed to parse:', jsonStr.substring(0, 500));

            // Try a more lenient approach for malformed JSON by cleaning common issues
            try {
              // Replace single quotes with double quotes and other common issues
              const cleanedJsonStr = jsonStr
                .replace(/'/g, '"')
                .replace(/(\w+):/g, '"$1":') // Convert unquoted keys to quoted keys
                .replace(/,\s*([}\]])/g, '$1') // Remove trailing commas
                // Remove any HTML-like content that might interfere
                .replace(/<\/?[^>]+(>|$)/g, '')
                // Normalize whitespace
                .replace(/\s+/g, ' ')
                // Fix missing commas that might occur if HTML tags were inserted between array items
                .replace(/}(\s*){/g, '},{')
                .replace(/](\s*)\[/g, '],[');

              const parsedJson = JSON.parse(cleanedJsonStr);
              return {
                jsonParams: parsedJson,
                remainingText: remainingText.substring(jsonEndPos + 1),
                jsonLength: jsonEndPos + 1 + leadingWhitespaceLength,
              };
            } catch (cleanupError) {
              console.error(`❌ Failed to clean and parse JSON for ${keyword}:`, cleanupError);
            }
          }
        }
      }
    } catch (error) {
      console.error(`❌ Error in JSON extraction process for ${keyword}:`, error);
    }

    return {jsonParams: null, remainingText, jsonLength: 0};
  };
  // Function to get component by keyword
  const getComponentForKeyword = (
    keyword: string,
    props: any = {},
    nodeText?: string,
    setRemainingText?: (text: string) => void,
    usePreExtractedParams: boolean = true
  ): React.ReactElement | null | undefined => {
    // Include event and eventOffers in props if they exist in options
    let localProps = {...props};
    let preview: boolean | undefined = false;
    let setSignUpRequiredOpen: any;
    let keywordsList: KeywordMapping[] = [];

    // Access the parseOptions from the parent function scope
    if (typeof parseOptions !== 'undefined') {
      if (parseOptions.event) localProps.event = parseOptions.event;
      if (parseOptions.eventOffers) localProps.eventOffers = parseOptions.eventOffers;
      preview = parseOptions.preview;
      setSignUpRequiredOpen = parseOptions.setSignUpRequiredOpen;
      keywordsList = parseOptions.keywordMappings;
    }
    const mapping = keywordsList.find((m) => m.keyword === keyword);
    if (!mapping) return null;

    // Extract JSON parameters if needed
    let extractedParams = {};
    if (mapping.extractParams && nodeText) {
      const extractResult = extractJsonParams(nodeText, keyword);
      if (extractResult.jsonParams) {
        extractedParams = extractResult.jsonParams;
        // If we have a function to set remaining text, use it to remove the JSON parameters
        if (setRemainingText && extractResult.jsonLength > 0) {
          const keywordPos = nodeText.indexOf(keyword);
          if (keywordPos !== -1) {
            const textWithoutJson =
              nodeText.substring(0, keywordPos + keyword.length) +
              nodeText.substring(keywordPos + keyword.length + extractResult.jsonLength);
            setRemainingText(textWithoutJson);
          }
        }
      }
    }    // Get the current instance index for this keyword only if we should use pre-extracted params
    let currentInstanceIndex = 0;
    let shouldUsePreExtracted = false;
    
    if (usePreExtractedParams && preExtractedParams[mapping.keyword]) {
      currentInstanceIndex = keywordInstanceCounters[mapping.keyword] || 0;
      shouldUsePreExtracted = currentInstanceIndex < preExtractedParams[mapping.keyword].length;
    }
    
    const component = mapping.component({
      ...(mapping.props || {}),
      ...localProps,
      ...(shouldUsePreExtracted
        ? preExtractedParams[mapping.keyword][currentInstanceIndex]
        : extractedParams),
      ...props, // Props passed directly should override everything else
      preview,
      setSignUpRequiredOpen,
      key: `component-${Math.random()}`,
    });

    // Increment the counter for this keyword only if we used pre-extracted params
    if (shouldUsePreExtracted) {
      keywordInstanceCounters[mapping.keyword] = currentInstanceIndex + 1;
    }

    // Ensure we only return valid types for html-react-parser
    if (component === null || component === undefined) return component;
    if (React.isValidElement(component)) return component;

    // If it's a string or other ReactNode type, wrap it in a span
    return <span>{component}</span>;
  };

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      // Case 1: Full paragraph containing only a keyword
      if (
        domNode instanceof Element &&
        domNode.name === 'p' &&
        domNode.children?.length === 1 &&
        domNode.children[0].type === 'text' &&
        'data' in domNode.children[0] &&
        typeof domNode.children[0].data === 'string'
      ) {
        const text = domNode.children[0].data.trim();
        for (const keyword of keywords) {
          if (text.startsWith(keyword)) {
            const mapping = keywordMappings.find((m) => m.keyword === keyword);            // Use pre-extracted params if available, otherwise try inline extraction as fallback
            if (preExtractedParams[keyword] && preExtractedParams[keyword].length > 0) {
              return getComponentForKeyword(keyword, {
                inline: false,
              });
            } else if (mapping?.extractParams) {
              const {jsonParams} = extractJsonParams(text, keyword);
              return getComponentForKeyword(keyword, {inline: false, ...(jsonParams || {})});
            } else if (text === keyword) {
              return getComponentForKeyword(keyword, {inline: false});
            }
          }
        }
      }

      // Case 2: Direct text node containing any keyword
      if (hasKeywordInTextNode(domNode, keywords)) {
        // Find which keywords are in this text
        const text = domNode.data;
        let result: React.ReactNode[] = [];
        let lastIndex = 0;

        for (const keyword of keywords) {
          const index = text.indexOf(keyword, lastIndex);
          if (index !== -1) {
            // Add text before the keyword
            if (index > lastIndex) {
              result.push(
                <React.Fragment key={`text-${result.length}`}>
                  {text.substring(lastIndex, index)}
                </React.Fragment>
              );
            }

            // Get the mapping to check if we need to extract JSON params
            const mapping = keywordMappings.find((m) => m.keyword === keyword);            // Add the component for this keyword
            if (preExtractedParams[keyword] && preExtractedParams[keyword].length > 0) {
              result.push(getComponentForKeyword(keyword, {}));
              // Move past just the keyword since JSON was already removed
              lastIndex = index + keyword.length;
            } else if (mapping?.extractParams) {
              // Fallback to inline extraction if pre-extraction didn't work
              const {jsonParams, jsonLength} = extractJsonParams(text.substring(index), keyword);
              result.push(getComponentForKeyword(keyword, {...(jsonParams || {})}));
              lastIndex = index + keyword.length + jsonLength;
            } else {
              result.push(getComponentForKeyword(keyword));
              lastIndex = index + keyword.length;
            }
          }
        }

        // Add any remaining text
        if (lastIndex < text.length) {
          result.push(
            <React.Fragment key={`text-${result.length}`}>
              {text.substring(lastIndex)}
            </React.Fragment>
          );
        }

        if (result.length > 0) {
          // Return as an element, not a fragment directly
          return React.createElement(React.Fragment, null, ...result);
        }
      }

      // Case 3: Handle elements that might contain keywords in children
      if (domNode instanceof Element && domNode.children) {
        // Check if any direct text child contains our keywords
        const hasKeywordsInChildren = domNode.children.some((child) =>
          hasKeywordInTextNode(child, keywords)
        );

        if (hasKeywordsInChildren) {
          // Create a modified version of children with components injected
          const newChildren: CustomNode[] = [];

          for (const child of domNode.children) {
            if (hasKeywordInTextNode(child, keywords)) {
              let lastIndex = 0;
              const text = child.data;

              for (const keyword of keywords) {
                const index = text.indexOf(keyword, lastIndex);
                if (index !== -1) {
                  // Add text before the keyword
                  if (index > lastIndex) {
                    newChildren.push({
                      ...child,
                      data: text.substring(lastIndex, index),
                    } as TextNodeWithData);
                  } // Use pre-extracted params if available, otherwise try inline extraction
                  const mapping = keywordMappings.find((m) => m.keyword === keyword);
                  let extractedParams = undefined;
                  let jsonLength = 0;                  if (preExtractedParams[keyword] && preExtractedParams[keyword].length > 0) {
                    // Use pre-extracted params - getComponentForKeyword will handle the counter
                    extractedParams = undefined; // Don't pass params here, let getComponentForKeyword handle it
                    jsonLength = 0;
                  } else if (mapping?.extractParams) {
                    console.log('🔍 Fallback to inline extraction for child element:', keyword);
                    const extractionResult = extractJsonParams(text.substring(index), keyword);
                    extractedParams = extractionResult.jsonParams;
                    jsonLength = extractionResult.jsonLength;
                  }

                  // Add a marker for the component position with params if available
                  newChildren.push({
                    type: 'component-marker',
                    key: `marker-${keyword}-${index}`,
                    keyword: keyword,
                    params: extractedParams,
                  } as ComponentMarkerNode);

                  // Move past this keyword and any JSON parameters
                  lastIndex = index + keyword.length + jsonLength;
                }
              }

              // Add any remaining text
              if (lastIndex < text.length) {
                newChildren.push({
                  ...child,
                  data: text.substring(lastIndex),
                } as TextNodeWithData);
              }
            } else {
              newChildren.push(child);
            }
          }

          // Now process the new children array, replacing markers with actual components
          const tagName = domNode.name;

          // Use React.createElement instead of JSX for dynamic tag names
          try {
            const childElements = newChildren
              .map((newChild, idx) => {
                if (newChild.type === 'component-marker') {
                  const markerNode = newChild as ComponentMarkerNode;
                  // Pass the extracted params to the component
                  return getComponentForKeyword(markerNode.keyword, {
                    key: `component-${idx}`,
                    ...(markerNode.params ? markerNode.params : {}),
                  });
                }
                // Cast to DOMNode to ensure type compatibility
                return domToReact([newChild as DOMNode], parserOptions);
              })
              .filter(Boolean);

            return React.createElement(tagName, domNode.attribs, ...childElements);
          } catch (err) {
            console.error('Error creating element:', err);
            return undefined;
          }
        }
      }

      return undefined;
    },
  };
  try {
    return parse(cleanedHtml, parserOptions);
  } catch (error) {
    console.error('Error parsing HTML with components:', error);
    return <div dangerouslySetInnerHTML={{__html: cleanedHtml}} />; // Fallback to cleaned html if parsing fails
  }
};
